import { useEffect, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { api, buildQueryString } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { Cloud } from 'lucide-react'
import { IncidentCard, type SupplyChainIncident } from './IncidentCard'
import type { FilterValues } from './SupplyChainFilters'

/**
 * Paginated feed for `/threats/supply-chain`. Mirrors the shape returned by
 * `GET /api/supply-chain/incidents`:
 *   - `incidents` — `IncidentListResponse[]` (see public-routes.ts)
 *   - `total`     — total non-suppressed rows matching the filter
 *   - `page`      — current page (1-indexed)
 *   - `pageSize`  — fixed at 20 server-side
 *
 * Uses `keepPreviousData` so the previous page stays visible while the next
 * one loads — avoids a layout thrash on Prev/Next clicks. Resets to page 1
 * whenever the filter values change (a new query key fires anyway, but the
 * pagination cursor would otherwise carry stale beyond-bounds page numbers).
 */
interface FeedResponse {
  incidents: SupplyChainIncident[]
  total: number
  page: number
  pageSize: number
}

function buildFeedParams(
  filters: FilterValues,
  page: number,
): Record<string, string | number> {
  const out: Record<string, string | number> = { page }
  if (filters.provider) out.provider = filters.provider
  if (filters.severity) out.severity = filters.severity
  if (filters.security_relevant !== undefined) {
    out.security_relevant = String(filters.security_relevant)
  }
  if (filters.from) out.from = filters.from
  if (filters.to) out.to = filters.to
  return out
}

export function SupplyChainFeed({ filters }: { filters: FilterValues }) {
  const [page, setPage] = useState(1)

  // Reset to page 1 whenever the filter set changes — keeps the page number
  // in sync with the filter. Using a JSON serialisation as the dependency
  // avoids referential-identity churn from the parent's useState `setFilters`.
  const filterKey = JSON.stringify(filters)
  useEffect(() => {
    setPage(1)
  }, [filterKey])

  const params = buildFeedParams(filters, page)
  const qs = buildQueryString(params)

  const { data, isLoading, isFetching, isError } = useQuery<FeedResponse>({
    queryKey: ['supply-chain', 'incidents', params],
    queryFn: () => api.get<FeedResponse>(`/supply-chain/incidents${qs}`),
    placeholderData: keepPreviousData,
  })

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="supply-chain-feed-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <EmptyState
        title="Couldn't load supply-chain incidents"
        description="The feed failed to load. Refresh to try again."
      />
    )
  }

  if (!data) return null

  if (data.incidents.length === 0) {
    return (
      <EmptyState
        icon={Cloud}
        title="No supply-chain incidents match these filters."
        description="Try clearing the filter bar or widening the date range."
      />
    )
  }

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize))

  return (
    <div className="space-y-3" data-testid="supply-chain-feed">
      <div
        className="space-y-3"
        aria-busy={isFetching || undefined}
        aria-live="polite"
      >
        {data.incidents.map((incident) => (
          <IncidentCard key={incident.id} incident={incident} />
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || isFetching}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </Button>
        <div className="text-sm text-muted-foreground tabular-nums">
          Page {page} of {totalPages} · {data.total} total
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages || isFetching}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
